import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import { h } from "vue";

// Helper to get toastr at runtime (loaded conditionally when Magellan is enabled)
const getToastr = () => window.toastr;

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);

    return {
      resolveTxt
    };
  },
  name: "CatFlashAlert",
  data: function () {
    return {
      toast: null
    };
  },
  props: {
    id: {
      type: [Number, String],
      default: null,
      description: "The node the error container is associated to."
    },
    level: {
      type: [Number],
      description:
        "The alert styling applied to the alert message wrapper (1-4)."
    },
    clearFlash: {
      type: Boolean,
      default: false,
      description:
        "Set this props to true to clear the flash alert programmatically."
    },
    messages: {
      type: [Array],
      description: "Displays error message."
    },
    title: {
      type: [String],
      description: "Adds header"
    },
    action: {
      type: String,
      default: null,
      description:
        "The label of the action button. If not provided, the button is not rendered at all."
    },
    actionClass: {
      type: String,
      default: "small secondary",
      description: "The classes to be set on the action button."
    },
    actionIcon: {
      type: String,
      description: "The icon to be set on the action button."
    },
    closeButton: {
      type: Boolean,
      default: true,
      description: "Adds close button"
    },
    closeButtonLabel: {
      type: String,
      description: "Alert close button label."
    },
    autoHide: {
      type: Boolean,
      default: false,
      description:
        "Indicates if the flash alert will be hidden automatically. Set to false to persist until selected."
    },
    autoHideTimeout: {
      type: String,
      default: "5000",
      description:
        "If the autoHide is on, indicates in millisecond how long the flash alert will display without user interaction."
    },
    autoHideExtendedTimeout: {
      type: String,
      default: "1000",
      description:
        "If the autoHide is on, indicates in millisecond how long the flash alert will display after a user hovers over it."
    },
    size: {
      type: String,
      default: "",
      description:
        "Values: full - whole screen width, small - small width, empty - content width"
    },
    position: {
      type: String,
      default: "top",
      description:
        "Values: Applicable to full and content size only: top, bottom. Applicable to small size only: top-right, top-center, top-left, bottom-right, bottom-center, bottom-left."
    },
    plain: {
      type: Boolean,
      default: false,
      description: "Renders the alert without any icons"
    },
    small: {
      type: Boolean,
      default: false,
      description: "Renders the alert with smaller icons and less padding"
    }
  },
  mounted: function () {
    this.showFlash();
  },
  watch: {
    clearFlash: function (newval, oldval) {
      if (newval !== oldval) {
        this.hideFlash();
      }
    }
  },
  methods: {
    hideFlash: function () {
      const toastr = getToastr();
      if (!toastr) return;
      if (this.clearFlash && this.toast) {
        toastr.clear(this.toast);
        this.$emit("ui-hide-flash");
      }
    },
    showFlash: function () {
      const toastr = getToastr();
      // Guard: if toastr is not available (Magellan disabled), emit hide and return
      if (!toastr) {
        this.$emit("ui-hide-flash");
        return;
      }

      var toast;
      switch (this.level) {
        case 4:
          toast = toastr.success;
          break;
        case 3:
          toast = toastr.info;
          break;
        case 2:
          toast = toastr.warning;
          break;
        default:
          toast = toastr.error;
      }
      var closeLabel = this.resolveTxt("skin.alert.button.close", "Close");

      var alert = toast(
        this.alertContent,
        '<div class="errors-container-icon" aria-hidden="true"></div>' +
          this.title,
        {
          escapeHtml: false,
          closeButton: this.closeButton,
          closeHtml: `<button type='button' class='button small simple right' data-label='${closeLabel}'>${closeLabel}</button>`,
          debug: false,
          newestOnTop: false,
          progressBar: false,
          positionClass: this.containerClass,
          preventDuplicates: false, // allowing duplicates to make sure this component displays the flash alert. It is observed that the toastr remembers the immediate previous message in the toasts list
          onclick: this.onClickFlash,
          showDuration: "300",
          hideDuration: "1000",
          timeOut: this.autoHide ? this.autoHideTimeout : "0",
          extendedTimeOut: this.autoHide ? this.autoHideExtendedTimeout : "0",
          showEasing: "swing",
          hideEasing: "linear",
          showMethod: "fadeIn",
          hideMethod: "fadeOut",
          onHidden: this.onHideFlash
        }
      );

      // Make sure the toast is actually created before adding additional attributes
      if (alert) {
        alert
          .attr("role", "alert")
          .attr("id", this.id)
          .attr("class", this.alertClass)
          .attr("data-label", this.alertDataLabel)
          .attr("data-label-type", this.alertDataLabelType);

        // Save the toast to component data, so it can be accessed later when hiding it programmatically via clear props
        this.toast = alert;
        this.handleFocus();
      } else {
        // The toaster was not created so let caller know
        this.$emit("ui-hide-flash");
      }
    },
    onHideFlash: function (event) {
      this.$emit("ui-hide-flash");
    },
    onClickFlash: function (event) {
      const actionBtn = document.getElementById(`${this.alertActionId}`);

      // See if there is an action and the target is the action
      if (this.action && event.target === actionBtn) {
        this.$emit("ui-click-action");
        // Removing focus after click so that flash alert is closable
        actionBtn.blur();
      } else {
        this.$emit("ui-click-flash");
      }
    },
    /**
     * Flash alert focus for screen readers
     * Focus to first focusable element only focus on keyUp
     * Eventlistener only listen once on flash alert display
     */
    handleFocus: function () {
      const handleEvent = function (e) {
        const focusableElem = jQuery(":focusable", "#toast-container").first();

        // Check first if there is a focusable element before focusing
        if (focusableElem) {
          focusableElem.focus();
        }

        // Remove event listener
        document.removeEventListener("keyup", handleEvent);
      };

      //Focus will be triggered on keyup
      document.addEventListener("keyup", handleEvent);
    }
  },
  computed: {
    containerClass: function () {
      // css:
      // * cat-flash-alert-wrapper - all
      // * opaque - all
      // * grid-container - full, content width
      // * toast-* - all
      // * <size> - all
      // * <position> - all

      const grid =
        this.size !== "small" && this.size !== "medium"
          ? " grid-container"
          : "";
      const toast =
        " toast-" +
        this.pos +
        (this.size !== "small" && this.size !== "medium" ? "-full-width" : "");

      return (
        "cat-flash-alert-wrapper opaque " +
        grid +
        toast +
        " " +
        this.size +
        " " +
        this.pos
      );
    },
    pos: function () {
      if (this.size !== "small" && this.size !== "medium") {
        if (["top", "bottom"].indexOf(this.position) < 0) {
          return this.position.startsWith("bottom") ? "bottom" : "top";
        } else {
          return this.position;
        }
      } else {
        return this.position;
      }
    },
    alertClass: function () {
      return (
        "cat-alert cat-flash-alert" +
        (this.plain ? " plain" : "") +
        (this.small ? " small" : "") +
        (this.level === 4 ? " success" : "") +
        (this.level === 3 ? " info" : "") +
        (this.level === 2 ? " warn" : "") +
        (this.level === 1 ? " error" : "")
      );
    },
    alertDataLabel: function () {
      return this.title ? this.title : this.messages.join("<br/>");
    },
    alertDataLabelType: function () {
      switch (this.level) {
        case 0:
          return "alert-fatal";
        case 1:
          return "alert-error";
        case 2:
          return "alert-warning";
        case 3:
          return "alert-info";
        case 4:
          return "alert-success";
        default:
          return "alert-default";
      }
    },
    alertActionId: function () {
      return this.id ? "flash-alert-action-" + this.id : "flash-alert-action";
    },
    alertContent: function () {
      let content = this.messages.join("<br/>");
      // Add the action button if present
      if (this.action) {
        let icon = this.actionIcon
          ? `<span aria-hidden="true" role="img" aria-label="${this.actionIcon}" class="cat-icon icon icon-${this.actionIcon}"></span>`
          : "";

        content +=
          `<div class="flash-alert-action">` +
          `<button id="${this.alertActionId}" type="button" role="button" data-button="${this.action}" data-label="${this.action}" class="button ${this.actionClass}">` +
          icon +
          this.action +
          `</button>` +
          `</div>`;
      }
      return content;
    }
  },
  render: function () {
    // Content is rendered by toastr in the body markup
    return h("div", { style: { display: "none" } });
  }
});
